import { useContext, useState, useEffect, useCallback } from "react";
import { useRecoilValue } from "recoil";
import { toast } from "sonner";

import Edit from "@mui/icons-material/Edit";
import LoadingButton from "@mui/lab/LoadingButton";
import { IconButton, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import { Translator } from "@/components/i18n";
import { accessTokenState } from "@/client/state";
import { GenAiContext } from "@/client/context";
import { ClientError } from "@/client/api";

interface Props {
  threadId: string;
  initialThreadName?: string; // Allow undefined
  fetchThreads: () => void;
}

const EditThreadButton = ({
  threadId,
  initialThreadName,
  fetchThreads,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [threadName, setThreadName] = useState(initialThreadName || ""); // Default to empty string
  const accessToken = useRecoilValue(accessTokenState);
  const apiClient = useContext(GenAiContext);
  const refetchThreads = useCallback(fetchThreads, []);

  useEffect(() => {
    if (open) {
      setThreadName(initialThreadName || ""); // Ensure it resets correctly
    }
  }, [open, initialThreadName]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (!threadName.trim() || threadName === initialThreadName) return; // Prevent empty or unchanged submissions

    toast.promise(apiClient.editThread(threadId, threadName, accessToken), {
      loading: (
        <Translator path="components.organisms.threadHistory.sidebar.EditThreadButton.renamingChat" />
      ),
      success: () => {
        refetchThreads();
        return (
          <Translator path="components.organisms.threadHistory.sidebar.EditThreadButton.chatRenamed" />
        );
      },
      error: (err) =>
        err instanceof ClientError ? <span>{err.message}</span> : <span></span>,
    });

    handleClose();
  };

  return (
    <div>
      <IconButton
        size="small"
        onClick={handleClickOpen}
        sx={{ p: "2px" }}
        color="inherit"
      >
        <Edit sx={{ width: 16, height: 16 }} />
      </IconButton>
      {open && (
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="edit-thread-title"
          PaperProps={{ sx: { backgroundImage: "none" } }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle id="edit-thread-title">{"Edit Thread"}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              variant="outlined"
              label="Thread Name"
              value={threadName}
              onChange={(e) => setThreadName(e.target.value)}
              autoFocus
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions sx={{ color: "text.secondary", p: 2 }}>
            <Button variant="outlined" color="inherit" onClick={handleClose}>
              <Translator path="components.organisms.threadHistory.sidebar.DeleteThreadButton.cancel" />
            </Button>
            <LoadingButton
              variant="outlined"
              color="primary"
              onClick={handleConfirm}
              disabled={!threadName.trim() || threadName === initialThreadName}
            >
              <Translator path="components.organisms.threadHistory.sidebar.DeleteThreadButton.confirm" />
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export { EditThreadButton };
